'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { FeatureCollection, Point } from 'geojson';
import * as maplibregl from 'maplibre-gl';
import { setWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MAPLIBRE_WORKER_URL } from '@/lib/maplibre-public-assets.mjs';
import {
  citationMapFitKey,
  fitCitedPointBounds,
  mapFocusFitKey,
  mapPointRole,
  shouldAutoFitMapCamera,
} from '@/shared/mobile-service/lib/map-bounds';
import type { MobileFrontEndGalleryView } from '@/shared/mobile-service/api/cors-mobile-api';
import type { MapPoint } from '@/shared/mobile-service/lib/map-points';
import { useOpenCollectedMedia } from '../media/collected-media';
import { PointStackPopup } from './PointStackPopup';
import {
  CLUSTER_MAX_ZOOM,
  CLUSTER_SOURCE_MAX_ZOOM,
  clusterClickOpensStack,
  nextClusterZoom,
  exactStackAt,
  pointsAtCoordinate,
  pointsForClusterLeaves,
  sameCoordinate,
  stackBadges,
  type ClusterLeaf,
} from './point-stack';

/**
 * AWS Location Service (Maps v2) style descriptors, served through the
 * authenticated same-origin tile proxy at ``/api/maps`` (SigV4 signed on the
 * server). The proxy forwards to ``maps.geo.<region>.amazonaws.com`` and never
 * exposes credentials to the browser.
 */
const STYLE_DESCRIPTORS: Record<'streets' | 'satellite', string> = {
  streets: '/api/maps/v2/styles/Standard/descriptor?color-scheme=Dark',
  satellite: '/api/maps/v2/styles/Satellite/descriptor',
};

const SOURCE_ID = 'mv-points';
const CLUSTER_LAYER = 'mv-clusters';
const CLUSTER_COUNT_LAYER = 'mv-cluster-count';
const POINT_LAYER = 'mv-unclustered';
const STACK_COUNT_LAYER = 'mv-stack-count';
const FIT_PADDING = 48;
const FIT_MAX_ZOOM = 16;

/**
 * Popup chrome is recolored here rather than in ``mobile-viewer.css``: that sheet is
 * scoped to ``.mv-root``, which the citation pane is not inside, so the popup kept
 * MapLibre's white background under this component's white text.
 */
const POPUP_CLASS = 'mv-map-popup';
const POPUP_BACKGROUND = '#0f1d32';

/**
 * Rewrite absolute AWS Location map URLs onto the same-origin proxy.
 *
 * The replacement must be an absolute URL. MapLibre loads vector tiles and
 * glyphs from a Web Worker created off a ``blob:`` URL, which has no base to
 * resolve a root-relative path against, so ``/api/maps/...`` dies there with
 * "Failed to parse URL" — leaving the vector basemap blank while main-thread
 * raster tiles (satellite) load fine.
 */
function transformRequest(url: string): { url: string } {
  const rewritten = url.replace(
    /^https:\/\/maps\.geo\.[^/]+\.amazonaws\.com\//,
    `${window.location.origin}/api/maps/`,
  );
  return { url: rewritten };
}

function toFeatureCollection(
  points: MapPoint[],
  focusIds: Array<string | number>,
  highlightIds: Array<string | number>,
): FeatureCollection<Point> {
  const hasCited = focusIds.length > 0;
  const badges = stackBadges(points);
  return {
    type: 'FeatureCollection',
    features: points.map((point, index) => {
      const role = mapPointRole(point.id, focusIds, highlightIds);
      const badge = badges[index];
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
        properties: {
          id: point.id,
          cited: role === 'cited' || role === 'highlight' ? 1 : 0,
          highlighted: role === 'highlight' ? 1 : 0,
          hasCited: hasCited ? 1 : 0,
          stackCount: badge?.stackCount ?? 1,
          stackLabel: badge?.showLabel ? String(badge.stackCount) : '',
          pointIndex: index,
        },
      };
    }),
  };
}

/**
 * Presentational MapLibre canvas. Callers supply already-mapped points;
 * {@link MapView} fetches a whole acquisition, {@link CitedMap} plots hydrate rows.
 *
 * Cited points stay full color; highlights get a ring; context is grayed.
 * Bounds fit every plotted point (1 km square for a single point) and retry
 * once the pane has a real size. A pan, zoom, or other camera gesture while
 * points are still arriving leaves that view in place; a later citation focus
 * still recenters.
 *
 * Markers that overlap on screen coalesce into one larger numbered circle.
 * Clicking that circle zooms until the group splits. At a high zoom, or when
 * the points sit on one coordinate, the click opens a prev/next stack instead.
 */
export function ArtifactMap({
  points,
  focusIds = [],
  highlightIds = [],
  detailFields,
  mediaPreview,
  heightClassName = 'h-[70vh]',
}: {
  points: MapPoint[];
  focusIds?: Array<string | number>;
  highlightIds?: Array<string | number>;
  detailFields: string[];
  /** Gallery view whose file a pin should preview. Absent when the artifact has no gallery. */
  mediaPreview?: MobileFrontEndGalleryView;
  heightClassName?: string;
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const dataRef = useRef<FeatureCollection<Point>>({
    type: 'FeatureCollection',
    features: [],
  });
  const lastFitKeyRef = useRef<string | null>(null);
  const cameraFocusKeyRef = useRef<string | null>(null);
  /** Set once the user pans, zooms, or otherwise takes the camera. */
  const userAdjustedCameraRef = useRef(false);
  const fitMapRef = useRef<(() => void) | null>(null);
  const revealCanvasRef = useRef<(() => void) | null>(null);
  const pointsRef = useRef(points);
  pointsRef.current = points;
  const [basemap, setBasemap] = useState<'streets' | 'satellite'>('streets');

  const pointsById = useMemo(() => {
    const map = new Map<string, MapPoint>();
    for (const point of points) map.set(point.id, point);
    return map;
  }, [points]);

  const pointsByIdRef = useRef(pointsById);
  pointsByIdRef.current = pointsById;
  const detailFieldsRef = useRef(detailFields);
  detailFieldsRef.current = detailFields;
  const mediaPreviewRef = useRef(mediaPreview);
  mediaPreviewRef.current = mediaPreview;
  const mediaRootRef = useRef<Root | null>(null);
  const { open, resolvePreview, resolvePreviewInfo } = useOpenCollectedMedia();
  const openRef = useRef(open);
  openRef.current = open;
  const resolvePreviewRef = useRef(resolvePreview);
  resolvePreviewRef.current = resolvePreview;
  const resolvePreviewInfoRef = useRef(resolvePreviewInfo);
  resolvePreviewInfoRef.current = resolvePreviewInfo;

  useEffect(() => {
    if (!container || mapRef.current) return;

    // v6 ESM worker must load from public/ (same-origin, unhashed sibling).
    setWorkerUrl(MAPLIBRE_WORKER_URL);

    const box = fitCitedPointBounds(pointsRef.current);
    const map = new maplibregl.Map({
      container,
      style: STYLE_DESCRIPTORS.streets,
      ...(box
        ? {
            bounds: [
              [box.west, box.south],
              [box.east, box.north],
            ] as [[number, number], [number, number]],
            fitBoundsOptions: { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM },
          }
        : { center: [0, 20] as [number, number], zoom: 1 }),
      maxZoom: CLUSTER_MAX_ZOOM,
      transformRequest,
      attributionControl: false,
    });
    mapRef.current = map;
    // The constructor camera is the points, but the style still paints the globe
    // until the first framed idle. Keep the canvas hidden until that frame.
    const canvas = map.getCanvas();
    canvas.style.visibility = 'hidden';
    let revealed = false;
    revealCanvasRef.current = () => {
      if (revealed || mapRef.current !== map) return;
      revealed = true;
      canvas.style.visibility = '';
    };
    // Gestures, the +/- control, and keyboard all carry originalEvent.
    // fitBounds and resize do not, so they keep auto-fitting until the user moves.
    map.on('movestart', (event) => {
      if (event.originalEvent) userAdjustedCameraRef.current = true;
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({}), 'bottom-left');
    map.addControl(new maplibregl.AttributionControl({ compact: true }));

    const ensureLayers = () => {
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: dataRef.current,
          cluster: true,
          clusterRadius: 50,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
          maxzoom: CLUSTER_SOURCE_MAX_ZOOM,
        });
      }
      if (!map.getLayer(CLUSTER_LAYER)) {
        map.addLayer({
          id: CLUSTER_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#2d8dfe',
            'circle-opacity': 0.85,
            'circle-radius': ['step', ['get', 'point_count'], 16, 25, 22, 100, 30],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          },
        });
      }
      if (!map.getLayer(CLUSTER_COUNT_LAYER)) {
        map.addLayer({
          id: CLUSTER_COUNT_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-size': 12,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#ffffff' },
        });
      }
      if (!map.getLayer(POINT_LAYER)) {
        map.addLayer({
          id: POINT_LAYER,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': [
              'case',
              ['==', ['get', 'highlighted'], 1],
              '#FBBF24',
              ['==', ['get', 'cited'], 1],
              '#FF6B5E',
              '#9CA3AF',
            ],
            'circle-radius': [
              'case',
              ['>', ['get', 'stackCount'], 1],
              ['step', ['get', 'stackCount'], 16, 25, 22, 100, 30],
              ['==', ['get', 'highlighted'], 1],
              8,
              6,
            ],
            'circle-opacity': [
              'case',
              ['==', ['get', 'hasCited'], 1],
              ['case', ['==', ['get', 'cited'], 1], 1, 0.45],
              1,
            ],
            'circle-stroke-width': ['case', ['==', ['get', 'highlighted'], 1], 3, 1.5],
            'circle-stroke-color': [
              'case',
              ['==', ['get', 'highlighted'], 1],
              '#F59E0B',
              '#ffffff',
            ],
          },
        });
      }
      if (!map.getLayer(STACK_COUNT_LAYER)) {
        map.addLayer({
          id: STACK_COUNT_LAYER,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'stackLabel'], '']],
          layout: {
            'text-field': ['get', 'stackLabel'],
            'text-size': 12,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: { 'text-color': '#ffffff' },
        });
      }
    };

    map.on('styledata', ensureLayers);
    const markReady = () => setMapReady(true);
    if (map.loaded()) markReady();
    else map.once('load', markReady);

    let suppressPointClick = false;
    const openPopup = (stack: MapPoint[], initialId: string | undefined, lngLat: [number, number]) => {
      if (stack.length === 0) return;
      mediaRootRef.current?.unmount();
      mediaRootRef.current = null;
      popupRef.current?.remove();
      popupRef.current = new maplibregl.Popup({
        closeButton: true,
        maxWidth: '360px',
        className: POPUP_CLASS,
      })
        .setLngLat(lngLat)
        .setHTML('<div data-point-popup-host></div>')
        .addTo(map);
      const host = popupRef.current.getElement()?.querySelector('[data-point-popup-host]');
      if (host) {
        const root = createRoot(host);
        mediaRootRef.current = root;
        root.render(
          <PointStackPopup
            points={stack}
            initialId={initialId}
            detailFields={detailFieldsRef.current}
            mediaPreview={mediaPreviewRef.current}
            resolvePreview={(fileUrl) => resolvePreviewRef.current(fileUrl)}
            resolvePreviewInfo={(fileUrl) => resolvePreviewInfoRef.current(fileUrl)}
            onOpen={(target) => openRef.current(target)}
          />,
        );
        popupRef.current.on('close', () => {
          if (mediaRootRef.current === root) {
            root.unmount();
            mediaRootRef.current = null;
          }
          suppressPointClick = true;
          queueMicrotask(() => {
            suppressPointClick = false;
          });
        });
      }
    };

    const zoomCluster = (center: [number, number], zoom: number) => {
      if (!Number.isFinite(zoom) || zoom <= map.getZoom()) return;
      userAdjustedCameraRef.current = true;
      map.easeTo({ center, zoom });
    };

    map.on('click', CLUSTER_LAYER, (event) => {
      if (suppressPointClick) return;
      const feature = event.features?.[0];
      if (!feature) return;
      const clusterId = Number(feature.properties?.cluster_id);
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (!Number.isFinite(clusterId) || !source || feature.geometry.type !== 'Point') return;
      const center: [number, number] = [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1],
      ];
      const pointCount = Number(feature.properties?.point_count);
      if (!Number.isFinite(pointCount) || pointCount <= 0) return;

      void (async () => {
        let expansionZoom = Number.NaN;
        try {
          expansionZoom = await source.getClusterExpansionZoom(clusterId);
        } catch {
          expansionZoom = Number.NaN;
        }
        if (mapRef.current !== map) return;

        const zoom = map.getZoom();
        // Page immediately when zoom will not separate the pins. Otherwise
        // peek at a few leaves so a wide cluster can step in without loading
        // every point, while an exact pile still opens the stack.
        const pageAtThisZoom = clusterClickOpensStack(zoom, expansionZoom, false);
        let leaves: ClusterLeaf[];
        try {
          if (!pageAtThisZoom) {
            const sample = await source.getClusterLeaves(clusterId, Math.min(pointCount, 8), 0);
            if (mapRef.current !== map) return;
            if (!sameCoordinate(sample)) {
              zoomCluster(center, nextClusterZoom(zoom, expansionZoom));
              return;
            }
            leaves =
              sample.length >= pointCount
                ? sample
                : await source.getClusterLeaves(clusterId, pointCount, 0);
          } else {
            leaves = await source.getClusterLeaves(clusterId, pointCount, 0);
          }
        } catch {
          zoomCluster(center, nextClusterZoom(zoom, expansionZoom));
          return;
        }
        if (mapRef.current !== map) return;

        if (!clusterClickOpensStack(zoom, expansionZoom, sameCoordinate(leaves))) {
          zoomCluster(center, nextClusterZoom(zoom, expansionZoom));
          return;
        }
        const exact = exactStackAt(pointsRef.current, leaves);
        const stack = exact ?? pointsForClusterLeaves(pointsRef.current, leaves);
        if (stack.length === 0) {
          zoomCluster(center, nextClusterZoom(zoom, expansionZoom));
          return;
        }
        openPopup(stack, undefined, center);
      })();
    });

    const openPoint = (feature: maplibregl.MapGeoJSONFeature | undefined) => {
      if (!feature || feature.geometry.type !== 'Point') return;
      const [longitude, latitude] = feature.geometry.coordinates;
      const rawId = feature.properties?.id;
      const id = rawId == null || rawId === '' ? undefined : String(rawId);
      const stack = pointsAtCoordinate(pointsRef.current, latitude, longitude);
      if (stack.length > 0) {
        openPopup(stack, id, [longitude, latitude]);
        return;
      }
      const point = id != null ? pointsByIdRef.current.get(id) : undefined;
      if (!point) return;
      openPopup([point], id, [point.longitude, point.latitude]);
    };

    const onPointClick = (
      event: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] },
    ) => {
      const target = event.originalEvent?.target;
      if (target instanceof Element && target.closest('.maplibregl-popup-close-button')) return;
      if (suppressPointClick) return;
      openPoint(event.features?.[0]);
    };
    // One listener: the badge sits on the circle, so separate layer handlers
    // both match the same click and the second tears down the popup the first opened.
    map.on('click', [POINT_LAYER, STACK_COUNT_LAYER], onPointClick);

    const setPointer = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const clearPointer = () => {
      map.getCanvas().style.cursor = '';
    };
    for (const layer of [CLUSTER_LAYER, POINT_LAYER, STACK_COUNT_LAYER]) {
      map.on('mouseenter', layer, setPointer);
      map.on('mouseleave', layer, clearPointer);
    }

    return () => {
      mediaRootRef.current?.unmount();
      mediaRootRef.current = null;
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
      lastFitKeyRef.current = null;
      cameraFocusKeyRef.current = null;
      userAdjustedCameraRef.current = false;
      revealCanvasRef.current = null;
      setMapReady(false);
    };
  }, [container]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(STYLE_DESCRIPTORS[basemap]);
  }, [basemap]);

  useEffect(() => {
    const collection = toFeatureCollection(points, focusIds, highlightIds);
    dataRef.current = collection;
    const map = mapRef.current;
    const fitKey = citationMapFitKey(points, focusIds, highlightIds);
    const apply = () => {
      if (mapRef.current !== map || !map) return;
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (source) source.setData(collection);
      const nextFocusKey = mapFocusFitKey(focusIds, highlightIds);
      const allowFit = shouldAutoFitMapCamera(
        userAdjustedCameraRef.current,
        cameraFocusKeyRef.current,
        nextFocusKey,
      );
      cameraFocusKeyRef.current = nextFocusKey;
      if (points.length === 0) return;
      if (!allowFit) {
        lastFitKeyRef.current = fitKey;
        revealCanvasRef.current?.();
        return;
      }
      if (lastFitKeyRef.current === fitKey) {
        revealCanvasRef.current?.();
        return;
      }
      const box = fitCitedPointBounds(points);
      if (!box) return;
      if (map.getContainer().clientWidth < 8 || map.getContainer().clientHeight < 8) return;
      map.resize();
      map.fitBounds(
        [
          [box.west, box.south],
          [box.east, box.north],
        ],
        { padding: FIT_PADDING, duration: 0, maxZoom: FIT_MAX_ZOOM },
      );
      lastFitKeyRef.current = fitKey;
      map.once('idle', () => revealCanvasRef.current?.());
    };
    fitMapRef.current = apply;
    if (!map || !mapReady) return;

    if (map.isStyleLoaded()) apply();
    else map.once('idle', apply);
  }, [points, focusIds, highlightIds, mapReady]);

  useEffect(() => {
    if (!container || !mapReady) return;
    const observer = new ResizeObserver(() => {
      const map = mapRef.current;
      if (!map) return;
      if (container.clientWidth < 8 || container.clientHeight < 8) return;
      map.resize();
      fitMapRef.current?.();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [container, mapReady]);

  return (
    <div className={`flex min-h-0 w-full flex-col gap-2 ${heightClassName}`}>
      <style>{POPUP_STYLES}</style>
      <div className="flex shrink-0 justify-end">
        <div className="inline-flex overflow-hidden rounded-md border border-white/20">
          {(['streets', 'satellite'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setBasemap(option)}
              className={`px-3 py-1.5 text-sm capitalize ${
                basemap === option
                  ? 'bg-brand-blue text-white'
                  : 'bg-white/5 text-white/80 hover:bg-white/10'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      <div
        ref={setContainer}
        className="min-h-0 w-full flex-1 overflow-hidden rounded-lg border border-white/10"
      />
    </div>
  );
}

const POPUP_STYLES = `
.${POPUP_CLASS} .maplibregl-popup-content {
  background: ${POPUP_BACKGROUND};
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  padding: 10px 12px;
}
.${POPUP_CLASS}[class*='anchor-top'] .maplibregl-popup-tip { border-bottom-color: ${POPUP_BACKGROUND}; }
.${POPUP_CLASS}[class*='anchor-bottom'] .maplibregl-popup-tip { border-top-color: ${POPUP_BACKGROUND}; }
.${POPUP_CLASS}.maplibregl-popup-anchor-left .maplibregl-popup-tip { border-right-color: ${POPUP_BACKGROUND}; }
.${POPUP_CLASS}.maplibregl-popup-anchor-right .maplibregl-popup-tip { border-left-color: ${POPUP_BACKGROUND}; }
.${POPUP_CLASS} .maplibregl-popup-close-button { color: rgba(255, 255, 255, 0.5); font-size: 16px; padding: 2px 6px; }
.${POPUP_CLASS} .maplibregl-popup-close-button:hover { color: rgba(255, 255, 255, 0.9); background: transparent; }
.mv-point-popup { font-size: 12px; line-height: 1.4; color: rgba(255, 255, 255, 0.9); }
.mv-point-nav { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 8px; }
.mv-point-nav button { border: 0; border-radius: 4px; background: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.9); padding: 2px 8px; cursor: pointer; }
.mv-point-nav button:hover { background: rgba(255, 255, 255, 0.18); }
.mv-point-title { font-weight: 600; font-size: 13px; margin-bottom: 2px; color: #ffffff; }
.mv-point-timestamp { color: rgba(255, 255, 255, 0.5); margin-bottom: 6px; }
.mv-point-row { display: flex; gap: 8px; justify-content: space-between; }
.mv-point-key { color: rgba(255, 255, 255, 0.5); }
.mv-point-val { font-weight: 500; text-align: right; word-break: break-word; color: rgba(255, 255, 255, 0.85); }
.mv-point-coords { color: rgba(255, 255, 255, 0.4); font-family: ui-monospace, monospace; margin-top: 6px; }
.mv-point-media { display: block; width: 100%; max-height: 180px; margin: 0 0 8px; border-radius: 6px; object-fit: contain; background: rgba(0, 0, 0, 0.35); }
audio.mv-point-media { max-height: none; }
`;
